import React, { useState, useEffect } from 'react';
import axios from 'axios';

const MessageLogs = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [phoneFilter, setPhoneFilter] = useState('');
  const [searchPhone, setSearchPhone] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  // Fetch messages
  const fetchMessages = async (page = 1, phone = '') => {
    setLoading(true);
    setError(null);
    
    try {
      let url = `${API_BASE_URL}/api/message-logs?page=${page}&limit=20`;
      
      if (phone) {
        url = `${API_BASE_URL}/api/message-logs/${phone}?page=${page}&limit=20`;
      }
      
      const response = await axios.get(url);
      setMessages(response.data.messages);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.totalCount);
    } catch (err) {
      setError('Failed to fetch message logs');
      console.error('Error fetching messages:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages(currentPage, searchPhone);
  }, [currentPage, searchPhone]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchPhone(phoneFilter);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setPhoneFilter('');
    setSearchPhone('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getDirectionClass = (direction) => {
    return `message-direction ${direction}`;
  };

  const getMessageTypeIcon = (type) => {
    switch (type) {
      case 'text': return '💬';
      case 'image': return '🖼️';
      case 'document': return '📄';
      case 'audio': return '🎵';
      case 'video': return '🎥';
      default: return '📱';
    }
  };

  return (
    <div>
      <div className="card">
        <h2 className="card-title">Message Logs</h2>
        
        {/* Search Filter */}
        <form onSubmit={handleSearch} style={{ marginBottom: '2rem' }}>
          <div className="form-group">
            <label className="form-label">Filter by Phone Number:</label>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
              <input
                type="text"
                className="form-input"
                value={phoneFilter}
                onChange={(e) => setPhoneFilter(e.target.value)}
                placeholder="Enter phone number (e.g., 6287780300386)"
                style={{ flex: 1 }}
              />
              <button type="submit" className="btn" disabled={loading}>
                Search
              </button>
              <button type="button" className="btn" onClick={clearSearch} disabled={loading}>
                Clear
              </button>
            </div>
          </div>
        </form>

        {/* Loading State */}
        {loading && <div className="loading">Loading messages...</div>}
        
        {/* Error State */}
        {error && <div className="error">{error}</div>}
        
        {/* Messages Count */}
        {!loading && !error && (
          <div className="pagination-info" style={{ marginBottom: '1rem' }}>
            Total Messages: {totalCount}
            {searchPhone && ` (filtered by: ${searchPhone})`}
          </div>
        )}
        
        {/* Messages List */}
        {!loading && !error && messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            No messages found
          </div>
        )}
        
        {!loading && !error && messages.length > 0 && (
          <div>
            {messages.map((message) => (
              <div key={message.id} className="message-item">
                <div className="message-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span className={getDirectionClass(message.direction)}>
                      {message.direction.toUpperCase()}
                    </span>
                    <span style={{ fontSize: '1.2rem' }}>
                      {getMessageTypeIcon(message.message_type)}
                    </span>
                    <span style={{ fontSize: '0.9rem', color: '#666' }}>
                      {message.message_type.toUpperCase()}
                    </span>
                  </div>
                  <div className="message-meta">
                    <span>Status: {message.status}</span>
                  </div>
                </div>
                
                <div style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}>
                  <div>
                    <strong>From:</strong> {message.sender_number}
                  </div>
                  <div>
                    <strong>To:</strong> {message.recipient_number}
                  </div>
                  {message.whatsapp_message_id && (
                    <div>
                      <strong>Message ID:</strong> {message.whatsapp_message_id}
                    </div>
                  )}
                </div>
                
                <div className="message-content">
                  {message.message_content}
                </div>
                
                <div className="message-meta">
                  <span>{formatDate(message.created_at)}</span>
                  <span>Session: {message.session_name || 'default'}</span>
                </div>
                
                {/* Raw Data Toggle */}
                <details style={{ marginTop: '0.5rem' }}>
                  <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: '#666' }}>
                    Show Raw Data
                  </summary>
                  <pre style={{ 
                    background: '#f0f0f0', 
                    padding: '0.5rem', 
                    borderRadius: '4px', 
                    fontSize: '0.7rem',
                    overflow: 'auto',
                    maxHeight: '200px',
                    marginTop: '0.5rem'
                  }}>
                    {JSON.stringify(JSON.parse(message.raw_data || '{}'), null, 2)}
                  </pre>
                </details>
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <div className="pagination">
            <button 
              className="btn" 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            
            <button 
              className="btn" 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
        
        {/* Auto Refresh */}
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button 
            className="btn" 
            onClick={() => fetchMessages(currentPage, searchPhone)}
            disabled={loading}
          >
            Refresh Messages
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageLogs;